/*
 * Lotus global add-to-cart handler.
 *
 * Intercepts every <form> POST to /cart/add anywhere in the store and routes the
 * response through <cart-drawer>.renderContents() so the cart DRAWER opens
 * instead of the cart PAGE — without requiring each form to be wrapped in
 * <product-form> or carry Dawn's specific span/spinner markup.
 *
 * If the cart type is set to "page" in theme settings, this script is not
 * loaded (gated in theme.liquid), so behavior falls back to native form POST.
 */
(function () {
  'use strict';

  function getCheckoutUrl() {
    // If a section has stashed a discount code on the page, route the customer
    // through Shopify's /discount/CODE?redirect=/checkout so the discount cookie
    // is set + applied on the way to the checkout page.
    var code = (typeof window.LOTUS_DISCOUNT_CODE === 'string' && window.LOTUS_DISCOUNT_CODE) || '';
    if (!code) return '/checkout';
    return '/discount/' + encodeURIComponent(code) + '?redirect=/checkout';
  }

  function getCartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    // Skip forms already handled by Dawn's <product-form> — its onSubmitHandler
    // calls preventDefault() and dispatches its own AJAX path.
    if (form.closest('product-form')) return false;
    var action = form.getAttribute('action') || '';
    if (!action) return false;
    // Match /cart/add and any localized prefix (e.g. /en-au/cart/add).
    return /\/cart\/add(\.js|\?|$)/.test(action) || action.indexOf(getCartAddUrl()) !== -1;
  }

  function showError(message) {
    // Soft-fail: log + alert. Dawn's per-form error wrappers aren't guaranteed
    // to exist on every card, so a minimal fallback is fine.
    console.error('[lotus add-to-cart]', message);
    if (message) {
      try {
        window.alert(message);
      } catch (e) { /* no-op */ }
    }
  }

  // Intercept the cart drawer's own 'Checkout' button so it routes through
  // /discount/CODE when a discount is stashed on the page. This catches the
  // standard drawer-checkout flow (after add-to-cart-drawer-open).
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('.cart-drawer-lotus__checkout');
    if (!btn) return;
    var url = getCheckoutUrl();
    if (url === '/checkout') return; // No discount code stashed; let Shopify handle it natively
    event.preventDefault();
    window.location.href = url;
  }, true);

  // Close the cart drawer when its overlay (the outside scrim) is clicked.
  // Delegated at the document level so it survives #CartDrawer innerHTML swaps
  // (add-to-cart re-render, cart updates, bfcache refresh) — Dawn binds the
  // close listener to the overlay element in connectedCallback, but that
  // element is replaced on every re-render, so once the cart has items the
  // direct binding is lost. This delegated handler keeps it working.
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var overlay = event.target.closest('#CartDrawer-Overlay, .cart-drawer-lotus__overlay');
    if (!overlay) return;
    var drawer = overlay.closest('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }, false);

  // Hero primary CTA mirrors the featured-product section's checkout button —
  // same product, variant, add-to-cart → checkout flow and discount — without
  // duplicating any of that logic. On click, find and trigger the featured
  // button so the hero auto-matches whatever the featured section sells.
  // Fallbacks: if no featured checkout button is on the page (or it's sold
  // out / disabled), the hero link's href (the product page) navigates as it
  // would with JS unavailable.
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var heroBtn = event.target.closest('[data-hero-checkout]');
    if (!heroBtn) return;
    var fpBtn = document.querySelector('.featured-product-lotus [data-fp-checkout]');
    if (fpBtn && !fpBtn.disabled) {
      event.preventDefault();
      fpBtn.click();
    }
    // else: let the hero link's href (the product page) handle navigation.
  }, false);

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isCartAddForm(form)) return;

      // The triggering button can opt this submission out of the drawer flow
      // and route straight to checkout instead. Read from event.submitter (the
      // actual button clicked) so multiple buy buttons in one form work.
      var submitterBtn = event.submitter || form.querySelector('[type="submit"]');
      var goToCheckout = !!(submitterBtn && submitterBtn.getAttribute('data-fp-checkout') === 'true');

      // "Go straight to checkout" still adds through /cart/add (AJAX, below) so
      // the line ACCUMULATES into the existing cart. The old cart-permalink
      // fast-path (/cart/{id}:{qty}) was dropped because that permalink REPLACES
      // the cart, so adding a second time only ever left one item at checkout.
      // After the add succeeds we redirect to checkout (via /discount/CODE when
      // a code is stashed) — see the goToCheckout branch after the fetch.

      var cartDrawer = document.querySelector('cart-drawer');
      // No drawer on the page (cart_type === 'page') — let the native POST run.
      if (!cartDrawer) return;

      event.preventDefault();

      var submitButton = submitterBtn || form.querySelector('[type="submit"]');
      var originalButtonHTML = null;
      var addingLabel = (submitButton && submitButton.getAttribute('data-adding-text')) || 'Adding';
      if (submitButton) {
        if (submitButton.getAttribute('aria-disabled') === 'true') return;
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading', 'is-adding');
        originalButtonHTML = submitButton.innerHTML;
        // Stash on the element too, so a bfcache restore (Back button) can
        // recover the original label via the pageshow handler below.
        if (submitButton.getAttribute('data-original-html') === null) {
          submitButton.setAttribute('data-original-html', originalButtonHTML);
        }
        submitButton.innerHTML =
          '<span class="lotus-add-spinner" aria-hidden="true"></span>' +
          '<span class="lotus-add-label">' + addingLabel + '</span>';
      }

      var formData = new FormData(form);
      if (cartDrawer && !goToCheckout) {
        try {
          var sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        } catch (e) { /* getSectionsToRender unavailable; continue without */ }
        cartDrawer.setActiveElement && cartDrawer.setActiveElement(document.activeElement);
      }

      var config = {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      fetch(getCartAddUrl(), config)
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) {
            showError(response.description || response.message || 'Could not add to cart');
            return;
          }
          // Notify any subscribers (cart-icon-bubble, etc.)
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response
            });
          }
          if (goToCheckout) {
            // Skip the drawer entirely and route through /discount/CODE if a
            // section has stashed one — Shopify applies the discount on the
            // way to checkout.
            window.location.href = getCheckoutUrl();
            return;
          }
          if (cartDrawer) cartDrawer.renderContents(response);
        })
        .catch(function (err) {
          showError(err && err.message);
        })
        .finally(function () {
          // Don't restore the button HTML if we're navigating away — avoids a
          // brief flicker back to the original price as the redirect kicks in.
          if (submitButton && !goToCheckout) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading', 'is-adding');
            if (originalButtonHTML !== null) submitButton.innerHTML = originalButtonHTML;
            submitButton.removeAttribute('data-original-html');
          }
        });
    },
    false
  );

  // ===== bfcache (Back/Forward cache) recovery =====
  // When a shopper adds to cart, goes to checkout, then hits Back, the browser
  // can restore this page from the bfcache in the exact (stale) state it was in
  // mid-navigation: the add button frozen on "Adding…" and the cart drawer/icon
  // showing pre-add contents. event.persisted === true means a bfcache restore,
  // so we (1) un-stick any button we left loading and (2) refresh the cart UI
  // from the server. No location.reload() — that would repaint prices in the
  // wrong market currency.
  function restoreStuckButtons() {
    var buttons = document.querySelectorAll('[data-original-html]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      btn.innerHTML = btn.getAttribute('data-original-html');
      btn.removeAttribute('data-original-html');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading', 'is-adding');
    }
  }

  function refreshCartUI() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return;
    fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        // Update the drawer + cart-icon bubble in place, reusing the component's
        // own selectors so the markup matches renderContents() exactly. We do
        // NOT call open() — the drawer just gets the correct contents silently.
        cartDrawer.getSectionsToRender().forEach(function (section) {
          var html = sections[section.id];
          if (!html) return;
          var el = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!el) return;
          try {
            el.innerHTML = cartDrawer.getSectionInnerHTML(html, section.selector);
          } catch (e) { /* selector missing in response; skip */ }
        });
        // Sync the empty/non-empty state on the <cart-drawer> element itself
        // (the inner-HTML swap above can't touch the host element's class).
        var drawerHTML = sections['cart-drawer'];
        if (drawerHTML) {
          var freshHost = new DOMParser()
            .parseFromString(drawerHTML, 'text/html')
            .querySelector('cart-drawer');
          if (freshHost) {
            cartDrawer.classList.toggle('is-empty', freshHost.classList.contains('is-empty'));
          }
        }
      })
      .catch(function () { /* best-effort; leave existing UI as-is */ });
  }

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    restoreStuckButtons();
    refreshCartUI();
  });
})();
